"use strict";

// see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/

const baseTemplate = document.createElement("template");
baseTemplate.innerHTML = require("./template");

// // load font via webpack asset/resource
// require("src/tokens/index.css");


// Save a list of named combobox actions, for future readability
const SelectActions = {
    Close: 0,
    CloseSelect: 1,
    First: 2,
    Last: 3,
    Next: 4,
    Open: 5,
    PageDown: 6,
    PageUp: 7,
    Previous: 8,
    Select: 9,
    Type: 10,
};

/*
 * Helper functions
 */

// filter an array of options against an input string
// returns an array of options that begin with the filter string, case-independent
function filterOptions(options = [], filter, exclude = []) {
    return options.filter((option) => {
        const label = option.hasOwnProperty("label") ? option.label : option;
        const matches = label.toLowerCase().indexOf(filter.toLowerCase()) === 0;
        return matches && exclude.indexOf(label) < 0;
    });
}

// map a key press to an action
function getActionFromKey(event, menuOpen) {
    const { key, altKey, ctrlKey, metaKey } = event;
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']; // all keys that will do the default open action
    // handle opening when closed
    if (!menuOpen && openKeys.includes(key)) {
        return SelectActions.Open;
    }

    // home and end move the selected option when open or closed
    if (key === 'Home') {
        return SelectActions.First;
    }
    if (key === 'End') {
        return SelectActions.Last;
    }

    // handle typing characters when open or closed
    if (
        key === 'Backspace' ||
        key === 'Clear' ||
        (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
    ) {
        return SelectActions.Type;
    }

    // handle keys when open
    if (menuOpen) {
        if (key === 'ArrowUp' && altKey) {
            return SelectActions.CloseSelect;
        } else if (key === 'ArrowDown' && !altKey) {
            return SelectActions.Next;
        } else if (key === 'ArrowUp') {
            return SelectActions.Previous;
        } else if (key === 'PageUp') {
            return SelectActions.PageUp;
        } else if (key === 'PageDown') {
            return SelectActions.PageDown;
        } else if (key === 'Escape') {
            return SelectActions.Close;
        } else if (key === 'Enter' || key === ' ') {
            return SelectActions.CloseSelect;
        }
    }
}

// return the index of an option from an array of options, based on a search string
// if the filter is multiple iterations of the same letter (e.g "aaa"), then cycle through first-letter matches
function getIndexByLetter(options, filter, startIndex = 0) {
    const orderedOptions = [
        ...options.slice(startIndex),
        ...options.slice(0, startIndex),
    ];
    const firstMatch = filterOptions(orderedOptions, filter)[0];
    const allSameLetter = (array) => array.every((letter) => letter === array[0]);

    // first check if there is an exact match for the typed string
    if (firstMatch) {
        return options.indexOf(firstMatch);
    }

    // if the same letter is being repeated, cycle through first-letter matches
    else if (allSameLetter(filter.split(''))) {
        const matches = filterOptions(orderedOptions, filter[0]);
        return options.indexOf(matches[0]);
    }

    // if no matches, return -1
    else {
        return -1;
    }
}

// get an updated option index after performing an action
function getUpdatedIndex(currentIndex, maxIndex, action) {
    const pageSize = 10; // used for pageup/pagedown

    switch (action) {
        case SelectActions.First:
            return 0;
        case SelectActions.Last:
            return maxIndex;
        case SelectActions.Previous:
            return Math.max(0, currentIndex - 1);
        case SelectActions.Next:
            return Math.min(maxIndex, currentIndex + 1);
        case SelectActions.PageUp:
            return Math.max(0, currentIndex - pageSize);
        case SelectActions.PageDown:
            return Math.min(maxIndex, currentIndex + pageSize);
        default:
            return currentIndex;
    }
}

// check if element is visible in browser view port
function isElementInView(element) {
    var bounding = element.getBoundingClientRect();

    return (
        bounding.top >= 0 &&
        bounding.left >= 0 &&
        bounding.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
        bounding.right <=
        (window.innerWidth || document.documentElement.clientWidth)
    );
}

// check if an element is currently scrollable
function isScrollable(element) {
    return element && element.clientHeight < element.scrollHeight;
}

// ensure a given child element is within the parent's visible scroll area
// if the child is not visible, scroll the parent
function maintainScrollVisibility(activeElement, scrollParent) {
    const { offsetHeight, offsetTop } = activeElement;
    const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent;

    const isAbove = offsetTop < scrollTop;
    const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight;

    if (isAbove) {
        scrollParent.scrollTo(0, offsetTop);
    } else if (isBelow) {
        scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight);
    }
}

/**
 * The native picker (opt-in `native` attribute) is used on touch screens only:
 * there the OS picker beats a custom list inside a scrolling dialog.
 */
const COARSE_POINTER_QUERY = "(pointer: coarse)";

function isCoarsePointer() {
    return typeof window.matchMedia === "function" && window.matchMedia(COARSE_POINTER_QUERY).matches;
}

class Dropdown extends HTMLElement {
    constructor(template) {
        super(template);

        this.root = this.attachShadow({ mode: "open" });

        const content = template ? template.content.cloneNode(true) : baseTemplate.content.cloneNode(true);
        this.root.appendChild(content);

        this.container = this.root.querySelector(".swiftref-dropdown");
        this.comboEl = this.root.querySelector("[role=combobox]");
        this.listboxEl = this.root.querySelector("[role=listbox]");

        // The open list is rendered in the TOP LAYER (Popover API) where the
        // browser has it, so no scrolling ancestor — a dialog's content box, a
        // card — can clip it; older engines keep the absolutely positioned list.
        this.usesTopLayer = typeof this.listboxEl.showPopover === "function";
        if (this.usesTopLayer) {
            this.listboxEl.setAttribute("popover", "manual");
        }
        this._repositionListbox = this.positionListbox.bind(this);

        this.labelElement = this.root.querySelector("label");
        this.messageElement = this.root.querySelector("swiftref-message");

        // state
        this.activeIndex = 0;
        this.currentValue = this.getAttribute("value");
        this.selectedOption;
        this.open = false;

        this.options = [];
        this.readSlots();
        this.init();

    }

    // connectedCallback() {
        // this.readSlots();
        // this.init();
    // }
    readSlots () {
        const options = this.querySelectorAll("[slot=option]");
        for (const option of options) {
            const label = option.innerText;
            const value = option.getAttribute("value") || label;
            const selected = option.hasAttribute("selected") || value === this.currentValue;
            if (selected) {
                this.selectedOption = option;
            }
            this.options.push({
                label: label,
                value: value,
                selected: selected
            });
            this.removeChild(option);
        }
    }
    init () {
        // select first option by default
        if (this.selectedOption) {
            if (this.getAttribute("kind") === "compact") {
                this.comboEl.innerHTML = this.selectedOption.value;
            } else {
                this.comboEl.innerHTML = this.selectedOption.label || this.selectedOption.value;
            }
        }

        // add event listeners
        this.addEventListener('blur', this.onComboBlur.bind(this));
        this.comboEl.addEventListener('click', this.onComboClick.bind(this));
        this.comboEl.addEventListener('keydown', this.onComboKeyDown.bind(this));

        // create options
        // this.options.forEach((option, index) => {
        //     const optionEl = this.createOption(option, index);
        //     this.listboxEl.appendChild(optionEl);
        // });
        this.renderOptions(this.options);
        this.renderNativePicker();

        // manage tooltip (if any)
        this.labelContainer = this.root.querySelector("div");
        const tooltipContainer = document.createElement("div");
        tooltipContainer.innerHTML = this.innerHTML;
        tooltipContainer.style.display = "inline-block";
        tooltipContainer.style.marginLeft = "4px";
        this.labelContainer.appendChild(tooltipContainer);
        this.innerHTML = "";
    }

    renderOptions(options) {

        if (!options || options.length === 0) {
            return;
        }
        this.listboxEl.innerHTML = "";
        // create options
        this.options.forEach((option, index) => {
            const optionEl = this.createOption(option, index);
            this.listboxEl.appendChild(optionEl);
        });
    }

    createOption (option, index) {
        const optionText = option.label;
        const optionEl = document.createElement("div");
        const kind = this.getAttribute("kind");

        if (kind === "multicheckbox") {
            const checkbox = document.createElement("input");
            checkbox.type = "checkbox";
            checkbox.value = option.label || option.value;
            checkbox.checked = option.selected;
            optionEl.appendChild(checkbox);
        }
        optionEl.setAttribute("role", "option");
        optionEl.className = 'combo-option';

        optionEl.setAttribute('aria-selected', `${option.selected}`);
        optionEl.appendChild(document.createTextNode(optionText));
        if (option.value) {
            optionEl.setAttribute("value", option.value);
        }

        const clickListener = this.onOptionClick.bind(this);
        optionEl.removeEventListener('click', clickListener);
        optionEl.addEventListener('click', clickListener);
        optionEl.addEventListener('mousedown', this.onOptionMouseDown.bind(this));

        return optionEl;
    }


    onComboType  (letter) {
        // open the listbox if it is closed
        this.updateMenuState(true);

        // find the index of the first matching option
        const searchString = this.getSearchString(letter);
        const searchIndex = getIndexByLetter(this.options, searchString, this.activeIndex + 1);
        // if a match was found, go to it
        if (searchIndex >= 0) {
            this.onOptionChange(searchIndex);
        }
        // if no matches, clear the timeout and search string
        else {
            window.clearTimeout(this.searchTimeout);
            this.searchString = '';
        }
    };

    getSearchString(char) {
        // reset typing timeout and start new timeout
        // this allows us to make multiple-letter matches, like a native select
        if (typeof this.searchTimeout === 'number') {
            window.clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = window.setTimeout(() => {
            this.searchString = '';
        }, 500);

        // add most recent letter to saved search string
        this.searchString += char;
        return this.searchString;
    };


    onComboBlur () {
        // do not do blur action if ignoreBlur flag has been set
        if (this.ignoreBlur) {
            this.ignoreBlur = false;
            return;
        }

        // select current option and close
        if (this.open) {
            this.selectOption(this.activeIndex);
            this.dispatchChangeEvent();
            this.updateMenuState(false, false);
        }
    }

    onComboClick () {
        if (this.disabled === false) {
            this.updateMenuState(!this.open, false);
        }
    }

    onComboKeyDown (event) {
        const { key } = event;
        const max = this.options.length - 1;

        const action = getActionFromKey(event, this.open);

        switch (action) {
            case SelectActions.Last:
            case SelectActions.First:
                this.updateMenuState(true);
            // intentional fallthrough
            case SelectActions.Next:
            case SelectActions.Previous:
            case SelectActions.PageUp:
            case SelectActions.PageDown:
                event.preventDefault();
                return this.onOptionChange(
                    getUpdatedIndex(this.activeIndex, max, action)
                );
            case SelectActions.CloseSelect:
                event.preventDefault();
                this.selectOption(this.activeIndex);
                this.dispatchChangeEvent();
            // intentional fallthrough
            case SelectActions.Close:
                event.preventDefault();
                return this.updateMenuState(false);
            case SelectActions.Type:
                return this.onComboType(key);
            case SelectActions.Open:
                event.preventDefault();
                return this.updateMenuState(true);
        }
    }

    onOptionMouseDown (event) {
        // Clicking an option will cause a blur event,
        // but we don't want to perform the default keyboard blur action
        this.ignoreBlur = true;
    }

    onOptionClick (event) {
        event.stopPropagation();
        const target = event.target;
        const index = Array.prototype.indexOf.call(target.parentNode.children, target);
        this.onOptionChange(index);
        this.selectOption(index);
        this.updateMenuState(false);

        /**
         * Do not dispatch the change event when the value is changed via javascript,
         * for example: dropdown.value = "ciao";
         * Just like regular selects, do not fire change event when the value is changed
         * programmatically, avoiding potential infinite loops.
         *
         * @author Simone Sacchi
         * @version 2025/02/21
         */
        if (event.pointerType !== "") {
            this.dispatchChangeEvent();
        }
    }

    dispatchChangeEvent () {
        this.dispatchEvent(new CustomEvent("change", {
            detail: {
                value: this.currentValue
            },
            bubbles: true
            // composed: true
        }));
    }


    selectOption (index) {
        // update state
        this.activeIndex = index;

        // to nothing in case of empty options
        if (this.options.length === 0) {
            return;
        }


        const option = this.options[index];
        this.currentValue = option.value || "";
        // update displayed value
        if (this.getAttribute("kind") === "compact") {
            this.comboEl.innerHTML = option.value;
        } else {
            this.comboEl.innerHTML = option.label || option.value;
        }

        // update aria-selected
        const options = this.container.querySelectorAll('[role=option]');
        [...options].forEach((optionEl) => {
            optionEl.setAttribute('aria-selected', 'false');
        });
        options[index].setAttribute('aria-selected', 'true');

        this.syncNativePicker();
    }


    updateMenuState (open, callFocus = true) {
        if (this.open === open) {
            return;
        }

        // update state
        this.open = open;

        // update aria-expanded and styles
        this.comboEl.setAttribute('aria-expanded', `${open}`);
        if (open) {
            this.container.classList.add('open');
            this.showListbox();
            this.dispatchEvent(new CustomEvent('dropdown-opened'));
        } else {
            this.container.classList.remove('open');
            this.hideListbox();
            this.dispatchEvent(new CustomEvent('dropdown-closed'));
        }

        // update activedescendant
        const activeID = open ? this.activeIndex : '';
        this.comboEl.setAttribute('aria-activedescendant', activeID);

        if (activeID === '' && !isElementInView(this.comboEl)) {
            this.comboEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {

            // scroll to the top of no option are selected in case of:
            // 1. open the dropdown
            // 2. scroll down and close it
            // 3. return on the top
            if (!this.currentValue) {
                this.listboxEl.scrollTop = 0;
            }
        }

        // move focus back to the combobox, if needed
        callFocus && this.comboEl.focus();

    }

    /**
     * Top-layer list: shown with the Popover API and placed from the combobox
     * rect, then kept in place while anything scrolls or the window resizes.
     * No-ops on engines without the API (the CSS keeps the absolute list).
     */
    showListbox () {
        if (!this.usesTopLayer) return;
        try {
            this.listboxEl.showPopover();
        } catch (e) {
            return;
        }
        this.positionListbox();
        window.addEventListener("scroll", this._repositionListbox, true);
        window.addEventListener("resize", this._repositionListbox);
    }

    hideListbox () {
        if (!this.usesTopLayer) return;
        window.removeEventListener("scroll", this._repositionListbox, true);
        window.removeEventListener("resize", this._repositionListbox);
        try {
            this.listboxEl.hidePopover();
        } catch (e) {}
    }

    /**
     * Under the combobox when the list fits there (or there is more room below
     * than above), otherwise above it; the height is capped to the room it
     * gets, the CSS max-height staying the upper bound.
     */
    positionListbox () {
        if (!this.open || !this.usesTopLayer) return;
        const anchor = this.comboEl.getBoundingClientRect();
        const list = this.listboxEl;
        const gap = 4;
        const cap = 300;
        const wanted = Math.min(list.scrollHeight, cap);
        const below = window.innerHeight - anchor.bottom - gap;
        const above = anchor.top - gap;
        list.style.left = `${anchor.left}px`;
        if (below >= wanted || below >= above) {
            list.style.top = `${anchor.bottom + gap}px`;
            list.style.bottom = "auto";
            list.style.maxHeight = `${Math.max(0, Math.min(cap, below))}px`;
        } else {
            list.style.top = "auto";
            list.style.bottom = `${window.innerHeight - anchor.top + gap}px`;
            list.style.maxHeight = `${Math.max(0, Math.min(cap, above))}px`;
        }
    }

    /**
     * Focuses the combobox (the host itself is not focusable), so a page can
     * move the focus between fields without reaching into the shadow root.
     * With the native picker in place, that is the control the user can open.
     */
    focus() {
        (this.nativeEl || this.comboEl).focus();
    }

    /**
     * Native picker — opt-in (`native` attribute), touch screens only: the
     * custom combobox stays the VISIBLE control, pixel for pixel, and a
     * transparent <select> with the same options lies over it and takes the
     * tap, so the OS opens its own picker instead of the custom list. Its
     * change goes through selectOption, like a click on a custom option, and
     * dispatches the same `change`. The custom list never opens on that path,
     * so dropdown-opened/dropdown-closed do not fire for the native picker.
     * Without the attribute, on a fine pointer or for the multicheckbox kind
     * nothing is created.
     */
    nativePickerWanted () {
        return this.hasAttribute("native") && this.getAttribute("kind") !== "multicheckbox" && isCoarsePointer();
    }

    renderNativePicker () {
        if (!this.nativePickerWanted()) {
            if (this.nativeEl) {
                this.nativeEl.remove();
                this.nativeEl = null;
            }
            return;
        }
        if (!this.nativeEl) {
            this.nativeEl = document.createElement("select");
            this.nativeEl.className = "native-picker";
            this.nativeEl.addEventListener("change", this.onNativeChange.bind(this));
            this.container.appendChild(this.nativeEl);
        }
        this.nativeEl.setAttribute("aria-label", this.getAttribute("label") || this.getAttribute("placeholder") || "");
        this.nativeEl.innerHTML = "";
        // the native option VALUE is the index: option values may be empty or repeated
        this.options.forEach((option, index) => {
            const optionEl = document.createElement("option");
            optionEl.value = String(index);
            optionEl.textContent = option.value === ""
                ? (this.getAttribute("placeholder") || "")
                : (option.label || option.value);
            this.nativeEl.appendChild(optionEl);
        });
        this.syncNativePicker();
    }

    syncNativePicker () {
        if (!this.nativeEl) {
            return;
        }
        const value = this.currentValue || "";
        const index = this.options.findIndex((option) => (option.value || "") === value);
        this.nativeEl.value = index >= 0 ? String(index) : "";
        this.nativeEl.disabled = this.disabled;
    }

    onNativeChange () {
        const index = Number(this.nativeEl.value);
        if (!Number.isInteger(index) || !this.options[index]) {
            return;
        }
        this.onOptionChange(index);
        this.selectOption(index);
        this.dispatchChangeEvent();
    }


    onOptionChange (index) {
        // update state
        this.activeIndex = index;

        // update aria-activedescendant
        this.comboEl.setAttribute('aria-activedescendant', index);

        // update active option styles
        const options = this.container.querySelectorAll('[role=option]');
        [...options].forEach((optionEl) => {
            optionEl.setAttribute('aria-selected', 'false');
        });
        options[index].setAttribute('aria-selected', 'true');

        // ensure the new option is in view
        if (isScrollable(this.listboxEl)) {
            maintainScrollVisibility(options[index], this.listboxEl);
        }

        // ensure the new option is visible on screen
        // ensure the new option is in view
        if (!isElementInView(options[index])) {
            options[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    static get observedAttributes() {
        return [
            "state",
            "message",
            "placeholder",
            "label",
            "value",
            "name",
            "disabled",
            "new_options",
            "native"
        ];
    }

    get value() {
        return this.currentValue || this.getAttribute("value");
    }
    set value(value) {
        this.currentValue = value;
        const options = this.listboxEl.querySelectorAll('[role="option"]');
        for (const option of options) {
            const optionValue = option.getAttribute("value") || "";
            if (optionValue === value) {
                option.click();
                return;
            }
        }


    }
    get message() {
        return this.hasAttribute("message");
    }
    set message(message) {
        this.setAttribute("message", message);
    }
    get placeholder() {
        return this.hasAttribute("placeholder");
    }
    set placeholder(placeholder) {
        this.setAttribute("placeholder", placeholder);
    }
    get label() {
        return this.hasAttribute("label");
    }
    set label(label) {
        this.setAttribute("label", label);
    }
    get name() {
        return this.getAttribute("name");
    }
    set name(name) {
        this.setAttribute("name", name);
    }
    get disabled() {
        return this.hasAttribute("disabled");
    }
    set disabled(disabled) {
        this.setAttribute("disabled", "");
    }
    get new_options() {
        try {
            return JSON.parse(this.getAttribute("new_options")) || [];
        } catch (e) {
            return [];
        }
    }
    set new_options(new_options) {
        try {
            this.setAttribute("new_options", JSON.stringify(new_options));
        } catch (e) {
            this.setAttribute("new_options", "[]");
        }
    }
    renderAttribute (name, value) {

        switch (name) {
            case "placeholder":
                this.comboEl.setAttribute("data-placeholder", value);
                this.comboEl.setAttribute("aria-label", value);
                break;
            case "message":
                if (value) {
                    this.messageElement.setAttribute("message", value);
                } else {
                    this.messageElement.removeAttribute("message");
                }
                break;
            case "state":
                if (value === "invalid") {
                    value = "error";
                }

                if (value) {
                    this.messageElement.setAttribute("state", value);
                } else {
                    this.messageElement.removeAttribute("state");
                }
                break;
            case "label":
                this.labelElement.innerText = value;
                break;
            case "new_options":
                if (this.new_options && this.new_options.length) {
                    this.options = this.new_options;
                }
                this.renderOptions(this.options);
                this.renderNativePicker();
                break;
            case "native":
                this.renderNativePicker();
                break;
            case "disabled":
                this.syncNativePicker();
                break;
            default:
                break;
        }
    }


    attributeChangedCallback(name, oldVal, newVal) {
        this.renderAttribute(name, newVal);
    }
}

window.customElements.define("swiftref-dropdown", Dropdown);

module.exports = Dropdown;
